import React, { useEffect, useState } from "react";
import { Card, Switch, Table, Typography, message, Space, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
    getAllModulesAction,
    getEntityModulesAction,
    saveEntityModulesAction,
} from "../../../../../../store/action/admin/moduleActions";

const { Text, Title } = Typography;

const ModulesPage = ({ entityId }) => {
    const dispatch = useDispatch();
    const [localModules, setLocalModules] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    // Get all modules from Redux
    const { modules, loading: modulesLoading } = useSelector(
        (state) => state.getAllModulesList || { modules: [], loading: false }
    );

    // Get entity-specific module assignments
    const { selectedModuleRoles, loading: entityModulesLoading } = useSelector(
        (state) =>
            state.getEntityModulesList || { selectedModuleRoles: {}, loading: false }
    );

    // Save state
    const { loading: saveLoading, success: saveSuccess } = useSelector(
        (state) => state.saveEntityModulesList || { loading: false }
    );

    // Load all modules and entity modules on mount
    useEffect(() => {
        if (entityId) {
            dispatch(getAllModulesAction());
            dispatch(getEntityModulesAction(entityId));
        }
    }, [dispatch, entityId]);

    // Sync local state with Redux data
    useEffect(() => {
        if (modules && modules.length > 0) {
            const enrichedModules = modules.map((module) => ({
                ...module,
                enabled: selectedModuleRoles.hasOwnProperty(module._id),
            }));
            setLocalModules(enrichedModules);
        }
    }, [modules, selectedModuleRoles]);

    // Handle save success
    useEffect(() => {
        if (saveSuccess) {
            message.success("Modules updated successfully");
            setHasChanges(false);
            // Reload entity modules
            dispatch(getEntityModulesAction(entityId));
        }
    }, [saveSuccess, dispatch, entityId]);

    const handleToggleModule = (moduleId, enabled) => {
        setLocalModules((prev) =>
            prev.map((mod) =>
                mod._id === moduleId ? { ...mod, enabled: enabled } : mod
            )
        );
        setHasChanges(true);
    };

    const handleSave = () => {
        // Build the selectedModuleRoles object
        const updatedModuleRoles = {};
        localModules.forEach((module) => {
            if (module.enabled) {
                // Keep existing roles if they exist, otherwise empty array
                updatedModuleRoles[module._id] =
                    selectedModuleRoles[module._id] || [];
            }
        });

        dispatch(saveEntityModulesAction(entityId, updatedModuleRoles));
    };

    const columns = [
        {
            title: "Module Name",
            dataIndex: "module_name",
            key: "module_name",
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{text}</Text>
                    {record.description && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.description}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: "Status",
            dataIndex: "enabled",
            key: "status",
            width: 120,
            render: (enabled) => (
                <Tag color={enabled ? "green" : "default"}>
                    {enabled ? "Enabled" : "Disabled"}
                </Tag>
            ),
        },
        {
            title: "Enable/Disable",
            key: "action",
            width: 150,
            render: (_, record) => (
                <Switch
                    checked={record.enabled}
                    onChange={(checked) => handleToggleModule(record._id, checked)}
                    loading={saveLoading}
                />
            ),
        },
    ];

    const loading = modulesLoading || entityModulesLoading;

    return (
        <Card
            title={
                <Space direction="vertical" size={0}>
                    <Title level={5} style={{ margin: 0 }}>
                        Entity Modules
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                        Enable or disable modules for users connected to this entity
                    </Text>
                </Space>
            }
            extra={
                hasChanges && (
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Unsaved changes
                        </Text>
                        <button
                            onClick={handleSave}
                            disabled={saveLoading}
                            style={{
                                padding: "4px 16px",
                                borderRadius: 6,
                                border: "1px solid #1890ff",
                                background: "#1890ff",
                                color: "white",
                                cursor: saveLoading ? "not-allowed" : "pointer",
                                fontSize: 14,
                            }}
                        >
                            {saveLoading ? "Saving..." : "Save Changes"}
                        </button>
                    </Space>
                )
            }
        >
            <Table
                columns={columns}
                dataSource={localModules}
                rowKey="_id"
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} modules`,
                }}
            />
        </Card>
    );
};

export default ModulesPage;
